import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadCloud, FileText } from 'lucide-react';

interface SmartDropZoneProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
}

export default function SmartDropZone({ onFileSelect, disabled }: SmartDropZoneProps) {
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles?.length > 0) {
      onFileSelect(acceptedFiles[0]);
    }
  }, [onFileSelect]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: false
  });

  return (
    <div
      {...getRootProps()}
      className={`
        relative border-2 border-dashed rounded-3xl p-12 text-center transition-all duration-300 cursor-pointer overflow-hidden
        
        /* Light Mode (Default) */
        bg-white border-gray-300 hover:border-blue-500 hover:bg-blue-50/50
        
        /* Dark Mode */
        dark:bg-slate-800/50 dark:border-slate-700 dark:hover:border-slate-500 dark:hover:bg-slate-800
        
        /* Drag Active State */
        ${isDragActive 
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10 scale-[1.02] shadow-xl shadow-blue-500/20' 
          : ''
        }
        
        /* Disabled State */
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}
    >
      <input {...getInputProps()} />
      
      {/* Decorative Gradient Blob (Adjusted opacity for Light Mode) */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/10 dark:bg-blue-500/20 rounded-full blur-3xl -z-10"></div>

      <div className="flex flex-col items-center justify-center space-y-4">
        {/* Icon Container */}
        <div className={`
          p-5 rounded-2xl transition-all duration-300
          ${isDragActive 
            ? 'bg-blue-500 text-white' 
            : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-400'
          }
        `}>
          {isDragActive ? <UploadCloud className="w-10 h-10 animate-bounce" /> : <FileText className="w-10 h-10" />}
        </div>
        
        {/* Text Content */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            {isDragActive ? 'Drop the docket here' : 'Drag & Drop Legal Filing'}
          </h3>
          <p className="text-gray-500 dark:text-slate-400 max-w-sm mx-auto">
            Upload PDF dockets, complaints, or exhibits. Our AI will automatically detect the jurisdiction and redaction rules.
          </p>
        </div>

        {/* Button */}
        <button className="px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm transition-colors shadow-lg shadow-blue-500/20 dark:shadow-blue-900/20">
          Browse Files
        </button>
      </div>
    </div>
  );
}